import { DragDropContext, DropResult } from '@hello-pangea/dnd';
import { Add } from '@mui/icons-material';
import { Typography } from '@mui/material';
import cn from 'classnames';
import { AnimatePresence } from 'framer-motion';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import { Button } from '@/components/Button';
import { useAlertsStore } from '@/configs/store';
import { useLocalStore } from '@/hooks/useLocalStore';
import { nextSprintDates } from '@/tracker/entities/sprint';
import { useTrackerStore } from '@/tracker/model';
import { Sprint } from '@/tracker/types';
import { ConfirmDialog } from '@/tracker/ui/ConfirmDialog';

import { BacklogColumn } from '../BacklogColumn';
import { CloseSprintDialog } from '../CloseSprintDialog';
import { SprintDialog } from '../SprintDialog';
import { BacklogStore } from '../../model/BacklogStore';
import { useBoardLayout } from '../../model/useBoardLayout';

import s from './BacklogContent.module.scss';

type DialogState =
  | { type: 'create' }
  | { type: 'edit' | 'close' | 'delete'; sprint: Sprint }
  | null;

/** Вкладка «Бэклог»: задачи без спринта и спринты, как в Яндекс Трекере */
const BacklogContent = observer(() => {
  const alertsStore = useAlertsStore();
  const trackerStore = useTrackerStore();
  const layout = useBoardLayout();
  const { store } = useLocalStore(() => new BacklogStore({ alertsStore, trackerStore, layout }));
  const [dialog, setDialog] = useState<DialogState>(null);

  useEffect(() => {
    store.load();
  }, [store]);

  const { board } = layout;

  const handleDragEnd = ({ draggableId, source, destination }: DropResult) => {
    if (!destination) return;

    store.moveTask({
      key: draggableId,
      from: source.droppableId,
      to: destination.droppableId,
      index: destination.index,
    });
  };

  if (store.isError) {
    return (
      <div className={s.empty}>
        <Typography color="text.secondary">Не удалось загрузить спринты</Typography>
        <Button onClick={store.load}>Повторить</Button>
      </div>
    );
  }

  const closingSprint = dialog?.type === 'close' ? dialog.sprint : null;
  const unfinishedCount = closingSprint ? store.unfinishedCount(closingSprint.id) : 0;

  return (
    <div className={s.tab}>
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className={cn(s.columns, store.isContentLoading && s.columns_loading)}>
          {store.columns.map((column) => (
            <BacklogColumn
              key={column.id}
              column={column}
              estimateBy={board?.estimateBy ?? null}
              canManage={store.canManage}
              hasActiveSprint={store.hasActiveSprint}
              isSaving={store.isSaving}
              onStart={(sprint) => store.startSprint(sprint.id)}
              onClose={(sprint) => setDialog({ type: 'close', sprint })}
              onEdit={(sprint) => setDialog({ type: 'edit', sprint })}
              onDelete={(sprint) => setDialog({ type: 'delete', sprint })}
            />
          ))}
          {store.canManage && (
            <button
              type="button"
              className={s.create}
              onClick={() => setDialog({ type: 'create' })}
            >
              <Add fontSize="small" />
              Создать спринт
            </button>
          )}
        </div>
      </DragDropContext>

      <AnimatePresence>
        {dialog?.type === 'create' && (
          <SprintDialog
            key="create"
            initial={{ name: '', ...nextSprintDates(store.sprints) }}
            isEdit={false}
            isSaving={store.isSaving}
            onSubmit={store.createSprint}
            onClose={() => setDialog(null)}
          />
        )}

        {dialog?.type === 'edit' && (
          <SprintDialog
            key="edit"
            initial={{
              name: dialog.sprint.name,
              startDate: dialog.sprint.startDate,
              endDate: dialog.sprint.endDate,
            }}
            isEdit
            isSaving={store.isSaving}
            onSubmit={(data) => store.updateSprint({ sprintId: dialog.sprint.id, data })}
            onClose={() => setDialog(null)}
          />
        )}

        {closingSprint && (
          <CloseSprintDialog
            key="close"
            sprint={closingSprint}
            targets={store.sprints.filter((sprint) => sprint.id !== closingSprint.id)}
            unfinishedCount={unfinishedCount}
            isSaving={store.isSaving}
            onSubmit={(moveUnfinishedTo) =>
              store.closeSprint({ sprintId: closingSprint.id, moveUnfinishedTo })
            }
            onClose={() => setDialog(null)}
          />
        )}

        {dialog?.type === 'delete' && (
          <ConfirmDialog
            key="delete"
            title={`Удалить «${dialog.sprint.name}»?`}
            text="Задачи спринта вернутся в бэклог доски."
            disabled={store.isSaving}
            onConfirm={async () => {
              if (await store.deleteSprint(dialog.sprint.id)) setDialog(null);
            }}
            onClose={() => setDialog(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
});

export { BacklogContent };
